import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type { ChangeEvent } from "react";
import { IconButton, Typography, Chip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchEntries,
  createEntry,
  updateEntry,
  removeEntry,
} from "@/store/entriesSlice";
import { fetchCategories } from "@/store/categoriesSlice";
import { fetchSubcategories } from "@/store/subcategoriesSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import { DEFAULT_CURRENCY, BRAND } from "@/constants/brand";
import type { Entry, EntryKind } from "@/types";
import { buildTrackerCsv, downloadCsvFile } from "@/utils/trackerCsv";
import { runTrackerCsvImport } from "@/utils/trackerCsvImport";

export type TrackerTab = "all" | EntryKind;

function todayISODate() {
  return new Date().toISOString().split("T")[0];
}

export function useTrackerPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { items: entries, loading } = useAppSelector((s) => s.entries);
  const { items: categories } = useAppSelector((s) => s.categories);
  const { items: subcategories } = useAppSelector((s) => s.subcategories);

  const [trackerTab, setTrackerTab] = useState<TrackerTab>("all");
  const [trackerCsvImporting, setTrackerCsvImporting] = useState(false);
  const trackerCsvInputRef = useRef<HTMLInputElement>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    categoryId: "",
    subcategoryId: "",
    amount: "",
    currency: DEFAULT_CURRENCY,
    spentAt: todayISODate(),
    entryKind: "expense" as EntryKind,
  });

  const resetForm = useCallback(
    (kind: EntryKind = trackerTab === "all" ? "expense" : trackerTab) => {
      setForm({
        name: "",
        description: "",
        categoryId: "",
        subcategoryId: "",
        amount: "",
        currency: DEFAULT_CURRENCY,
        spentAt: todayISODate(),
        entryKind: kind,
      });
    },
    [trackerTab],
  );

  useEffect(() => {
    dispatch(fetchEntries());
    dispatch(fetchCategories());
    dispatch(fetchSubcategories());
  }, [dispatch]);

  const displayedEntries = useMemo(() => {
    if (trackerTab === "all") return entries;
    return entries.filter((e) => e.kind === trackerTab);
  }, [entries, trackerTab]);

  const openCreate = useCallback(() => {
    setEditingEntry(null);
    const presetKind: EntryKind = trackerTab === "all" ? "expense" : trackerTab;
    resetForm(presetKind);
    setDialogOpen(true);
  }, [resetForm, trackerTab]);

  const openEdit = useCallback((entry: Entry) => {
    setEditingEntry(entry);
    setForm({
      name: entry.name ?? "",
      description: entry.description || "",
      categoryId: entry.categoryId,
      subcategoryId: entry.subcategoryId || "",
      amount: String(entry.amount),
      currency: entry.currency,
      spentAt: entry.spentAt,
      entryKind: entry.kind ?? "expense",
    });
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedName = form.name.trim();
    const trimmedDescription = form.description.trim();
    const data = {
      name: trimmedName ? trimmedName : null,
      description: trimmedDescription || undefined,
      categoryId: form.categoryId,
      subcategoryId:
        form.subcategoryId.trim() === "" ? null : form.subcategoryId.trim(),
      amount: parseFloat(form.amount),
      currency: form.currency.trim(),
      spentAt: form.spentAt,
    };
    try {
      if (editingEntry) {
        await dispatch(updateEntry({ id: editingEntry.id, data })).unwrap();
      } else {
        await dispatch(createEntry(data)).unwrap();
      }
      setDialogOpen(false);
      setEditingEntry(null);
      resetForm();
      dispatch(fetchEntries());
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  }, [dispatch, editingEntry, form, notify, resetForm]);

  const handleDelete = useCallback(async () => {
    if (deleteId) {
      try {
        await dispatch(removeEntry(deleteId)).unwrap();
        setDeleteId(null);
      } catch (err) {
        notify(extractErrorMessage(err), "error");
      }
    }
  }, [deleteId, dispatch, notify]);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingEntry(null);
    resetForm();
  }, [resetForm]);

  const effectiveFormKind: EntryKind =
    trackerTab === "all" ? form.entryKind : trackerTab;

  const categoriesForForm = useMemo(
    () => categories.filter((c) => c.kind === effectiveFormKind),
    [categories, effectiveFormKind],
  );

  const filteredSubs = useMemo(
    () => subcategories.filter((s) => s.categoryId === form.categoryId),
    [subcategories, form.categoryId],
  );

  const showKindToggle = trackerTab === "all";

  const exportCsv = useCallback(() => {
    const csv = buildTrackerCsv(displayedEntries);
    const scope = trackerTab === "all" ? "all" : trackerTab;
    downloadCsvFile(`tracker-export-${scope}-${todayISODate()}.csv`, csv);
  }, [displayedEntries, trackerTab]);

  const onTrackerCsvFileSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setTrackerCsvImporting(true);
      try {
        const text = await file.text();
        const result = await runTrackerCsvImport(dispatch, text);
        if (result.fatalReason === "bad_header") {
          notify(t("tracker.importCsvBadHeader"), "error", 6500);
          return;
        }
        if (result.fatalReason === "empty_file") {
          notify(t("tracker.importCsvEmpty"), "warning");
          return;
        }
        if (
          result.created === 0 &&
          result.skipped === 0 &&
          result.errors.length === 0
        ) {
          notify(t("tracker.importCsvNoDataRows"), "info");
          return;
        }
        const summary =
          result.skipped > 0
            ? t("tracker.importCsvSummaryPartial", {
                created: result.created,
                skipped: result.skipped,
              })
            : t("tracker.importCsvSummaryOk", {
                created: result.created,
              });
        notify(
          summary,
          result.skipped > 0 || result.errors.length > 0
            ? "warning"
            : "success",
          6500,
        );
        if (result.errors.length > 0) {
          notify(
            t("tracker.importCsvErrorsPreview", {
              list: result.errors.slice(0, 6).join(" · "),
            }),
            "info",
            12000,
          );
        }
      } catch (err) {
        notify(extractErrorMessage(err), "error");
      } finally {
        setTrackerCsvImporting(false);
      }
    },
    [dispatch, notify, t],
  );

  const pickTrackerCsvFile = useCallback(() => {
    trackerCsvInputRef.current?.click();
  }, []);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "kind",
        headerName: t("tracker.type"),
        width: 110,
        sortable: false,
        valueGetter: (_value, row: Entry) =>
          row.kind === "gain"
            ? t("tracker.gainsLabel")
            : t("tracker.expensesLabel"),
        renderCell: (params) => (
          <Chip
            size="small"
            variant="outlined"
            label={
              params.row.kind === "gain"
                ? t("tracker.gainsLabel")
                : t("tracker.expensesLabel")
            }
            sx={{
              borderColor:
                params.row.kind === "gain" ? BRAND.green : BRAND.danger,
              color: params.row.kind === "gain" ? BRAND.green : BRAND.danger,
              fontWeight: 600,
              width: "100%",
              height: "auto",
              minHeight: 28,
              alignSelf: "center",
              "& .MuiChip-label": { px: 1, lineHeight: 1.25 },
            }}
          />
        ),
      },
      {
        field: "name",
        headerName: t("common.name"),
        flex: 1,
        minWidth: 150,
        valueGetter: (_value, row) => row.name ?? "",
      },
      {
        field: "category",
        headerName: t("common.category"),
        flex: 1,
        minWidth: 130,
        valueGetter: (_value, row) => row.category?.name || "",
      },
      {
        field: "subcategory",
        headerName: t("common.subcategory"),
        flex: 1,
        minWidth: 130,
        valueGetter: (_value, row) => row.subcategory?.name || "",
      },
      {
        field: "amount",
        headerName: t("common.amount"),
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const isGain = params.row.kind === "gain";
          const n = Number(params.row.amount);
          const text = Number.isNaN(n) ? "-" : n.toFixed(2);
          return (
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isGain ? BRAND.green : BRAND.danger,
                width: "100%",
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.43,
                display: "block",
              }}
            >
              {isGain ? "+" : "−"}
              {text}
            </Typography>
          );
        },
      },
      { field: "currency", headerName: t("common.currency"), width: 90 },
      { field: "spentAt", headerName: t("common.date"), width: 120 },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton size="small" onClick={() => openEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setDeleteId(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [t, openEdit],
  );

  return {
    t,
    entries: displayedEntries,
    loading,
    categories,
    categoriesForForm,
    columns,
    dialogOpen,
    setDialogOpen,
    editingEntry,
    closeDialog,
    form,
    setForm,
    deleteId,
    setDeleteId,
    openCreate,
    handleSave,
    handleDelete,
    filteredSubs,
    trackerTab,
    setTrackerTab,
    showKindToggle,
    effectiveFormKind,
    exportCsv,
    trackerCsvInputRef,
    trackerCsvImporting,
    onTrackerCsvFileSelected,
    pickTrackerCsvFile,
  };
}
